import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ResolveTicketModal({ open, onOpenChange, ticket, targetStatus = "resolved", onResolved }) {
  const [reasons, setReasons] = useState([]);
  const [reasonId, setReasonId] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/closure-reasons").then((r) => setReasons(r.data.filter((x) => x.active)));
  }, [open]);

  const submit = async () => {
    if (!reasonId) return toast.error("Pick a closure reason");
    setSubmitting(true);
    try {
      const reason = reasons.find((r) => r.id === reasonId);
      const { data } = await api.post(`/tickets/${ticket.id}/resolve`, {
        closure_reason_id: reasonId,
        closure_reason_name: reason?.name || "",
        notes,
        status: targetStatus,
      });
      toast.success(`${ticket.code} → ${targetStatus}`);
      onResolved?.(data);
      onOpenChange(false);
      setReasonId(""); setNotes("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const label = targetStatus === "closed" ? "Close" : "Resolve";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="resolve-ticket-modal" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-emerald-700">
            <CheckCircle2 className="w-4 h-4" /> {label} ticket
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Pick a closure reason for <span className="font-mono">{ticket?.code}</span>. The reason is
            visible on the ticket timeline and audit log.
          </p>
        </DialogHeader>
        <div className="py-2 space-y-4">
          <div>
            <Label className="text-xs font-semibold text-slate-700">Closure reason *</Label>
            <Select value={reasonId} onValueChange={setReasonId}>
              <SelectTrigger className="mt-1.5" data-testid="closure-reason-select">
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Resolution notes (optional)</Label>
            <Textarea
              data-testid="closure-notes-input"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What was done? Customer feedback? Reference numbers?"
              className="mt-1.5 resize-none"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="closure-cancel-btn">
            Back
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !reasonId}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="closure-submit-btn"
          >
            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
            {submitting ? "Saving..." : `${label} ticket`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
